/** Uniform height for corridor promo, placeholder, and head cards in carousels and explore rows. */
export const ECOSYSTEM_CORRIDOR_CARD_HEIGHT = 280;

/** Fixed size for cards in the global corridor explore horizontal rows. */
export const ECOSYSTEM_EXPLORE_CARD_WIDTH = 300;
export const ECOSYSTEM_EXPLORE_CARD_HEIGHT = 280;
export const ECOSYSTEM_EXPLORE_CARD_HEADER_HEIGHT = 64;
export const ECOSYSTEM_EXPLORE_CARD_GRID_GAP = 24;

/**
 * Container-query breakpoints (keys are the grid's own available width, not the viewport).
 * Keeps each card near ~300px wide and wraps remaining cards to the next line when the
 * available space (e.g. with the sidebar expanded) is narrow. Up to 5 cards per row on a wide container.
 * NOTE: consumers must render the grid with `type="container"`.
 */
export const ECOSYSTEM_EXPLORE_CARD_GRID_COLS = {
  base: 1,
  "620px": 2,
  "940px": 3,
  "1260px": 4,
  "1580px": 5,
} as const;

export const ECOSYSTEM_CORRIDOR_HEAD_CONTACT_EMAIL = "team@gtsetu.com";

export const ECOSYSTEM_CORRIDOR_HEAD_MAILTO = `mailto:${ECOSYSTEM_CORRIDOR_HEAD_CONTACT_EMAIL}?subject=${encodeURIComponent("Ecosystem Corridor Head Inquiry")}`;

export type EcosystemCorridorHeadDetailSection = {
  title: string;
  body: string;
};

export const ECOSYSTEM_CORRIDOR_HEAD_DETAIL_SECTIONS: EcosystemCorridorHeadDetailSection[] = [
  {
    title: "HOW IT WORKS",
    body: "Whether you represent a sector, a business network or possess specialized expertise, companies can engage with your ecosystem to explore global partnerships, market-entry opportunities, distribution channels, technology collaborations, sourcing relationships and strategic business connections.",
  },
  {
    title: "HOW YOU EARN",
    body: "Generate opportunities through your expertise and ecosystem.",
  },
  {
    title: "REQUIREMENTS",
    body: "We look for experienced professionals, consultants, industry leaders, associations, chambers and ecosystem builders with established sector knowledge and business networks.",
  },
];

export type CorridorListEntry<T> =
  | { type: "item"; item: T; key: string }
  | { type: "promo"; key: string };

export type BuildCorridorEntriesOptions = {
  /** Insert one promo card after every N items (default 2). */
  promoEveryN?: number;
};

function interleavePromoEveryN<T>(
  items: T[],
  itemKey: (item: T) => string,
  everyN: number,
): CorridorListEntry<T>[] {
  const result: CorridorListEntry<T>[] = [];

  items.forEach((item, index) => {
    result.push({ type: "item", item, key: itemKey(item) });
    if ((index + 1) % everyN === 0) {
      result.push({ type: "promo", key: `promo-after-${index}` });
    }
  });

  return result;
}


export function buildEcosystemCorridorEntries<T>(
  realItems: T[],
  placeholderItems: T[],
  itemKey: (item: T) => string,
  options?: BuildCorridorEntriesOptions,
): CorridorListEntry<T>[] {
  const promoEveryN = Math.max(1, options?.promoEveryN ?? 2);

  if (realItems.length === 0) {
    const result: CorridorListEntry<T>[] = [{ type: "promo", key: "promo" }];
    for (const item of placeholderItems) {
      result.push({ type: "item", item, key: itemKey(item) });
    }
    return result;
  }

  const allItems = [...realItems, ...placeholderItems];

  if (allItems.length >= promoEveryN) {
    return interleavePromoEveryN(allItems, itemKey, promoEveryN);
  }

  const result: CorridorListEntry<T>[] = [];

  for (const item of realItems) {
    result.push({ type: "item", item, key: itemKey(item) });
  }
  result.push({ type: "promo", key: "promo" });
  for (const item of placeholderItems) {
    result.push({ type: "item", item, key: itemKey(item) });
  }

  return result;
}
